import {afterNextRender, Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject, Subscription, take} from 'rxjs';
import {PostTypeEnum} from '../../core/enums/post-type.enum';
import {ViewTypeEnum} from '../../core/enums/view-type.enum';
import {TimelineGridColumnsConfig, TimelineStateSnapshot} from '../../shared/timeline/timeline.component';
import {TimelineDataProviderService, TimelinePageResult} from '../../shared/timeline/timeline.data-provider.service';
import {SeoService} from "../../core/services/seo.service";
import {TranslateService} from "@ngx-translate/core";
import {environment} from "../../../environments/environment";
import {FeedPageResult} from '../../shared/feed/feed-collection-data-provider.service';
import {FeedApiModel} from '../../api/models/feed.api.model';
import {HomeContentPrefetchService, HomeContentTab} from './home-content-prefetch.service';
import {ModalService} from '../../shared/modal/modal.service';
import {MediaTypeEnum} from '../../core/enums/media-type.enum';
import {TokenDataProviderService} from '../../core/services/token-data-provider.service';
import {CurrentUserViewModel} from '../../core/models/current-user.view-model';
import {PostViewModel} from '../../shared/timeline/post.view-model';
import {TimelineComponent} from '../../shared/timeline/timeline.component';
import {VideoService} from '../../shared/timeline/video.service';
import {MediaOfflineService} from '../../core/services/media-offline.service';
import {MediaObjectPipe} from '../../core/pipes/media-object.pipe';
import {
  HomeTabSwipeCommit,
  HomeTabSwipeDirection,
  HomeTabSwipeDirective,
  HomeTabSwipePreview,
} from '../../shared/directives/home-tab-swipe.directive';

interface HomeTabPanel {
  id: HomeContentTab;
  offsetPercent: number;
  renderMode: 'active' | 'swipe-preview';
  role: 'active' | 'preview' | 'promoting';
  previewMediaEnabled: boolean;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild(TimelineComponent) private activeTimeline?: TimelineComponent;
  @ViewChild(HomeTabSwipeDirective) private homeTabSwipe?: HomeTabSwipeDirective;
  public readonly tabs: Array<{id: HomeContentTab; label: string}> = [
    {id: 'timeline', label: 'features.home.tabs.timeline'},
    {id: 'feed', label: 'features.home.tabs.feed'},
    {id: 'media', label: 'features.home.tabs.media'},
    {id: 'reels', label: 'features.home.tabs.reels'},
  ];
  public readonly timelinePostTypes: PostTypeEnum[] = [
    PostTypeEnum.SIMPLE_POST,
    PostTypeEnum.PROJECT_POST,
    PostTypeEnum.EVENT_DISCUSSION_POST,
    PostTypeEnum.PROJECT_MEDIA_POST,
  ];
  public readonly mediaPostTypes: PostTypeEnum[] = [PostTypeEnum.PROJECT_MEDIA_POST];
  public readonly mediaGridColumns: TimelineGridColumnsConfig = {default: 3};
  public readonly ViewTypeEnum = ViewTypeEnum;
  public readonly MediaTypeEnum = MediaTypeEnum;
  public activeTab: HomeContentTab = 'timeline';
  public tabTransitionDirection: 'next' | 'previous' | null = null;
  public swipePreviewPanels: Array<{tab: HomeContentTab; offsetPercent: number; previewMediaEnabled: boolean}> = [];
  public homeTabPanels: HomeTabPanel[] = [];
  public readonly preloadPending: Record<HomeContentTab, boolean> = {timeline: false, feed: false, media: false, reels: false};
  public readonly initialPageRequested: Record<HomeContentTab, boolean> = {timeline: false, feed: false, media: false, reels: false};
  /** Retains API models and scroll only; the corresponding timeline instance is destroyed off-tab. */
  public readonly timelineStates: Record<'timeline' | 'media', TimelineStateSnapshot | null> = {
    timeline: null,
    media: null,
  };
  public timelinePage: TimelinePageResult | null = null;
  public mediaPage: TimelinePageResult | null = null;
  public feedPage: FeedPageResult | null = null;
  public readonly feedLoadingPage: FeedPageResult = {
    records: [Object.assign(new FeedApiModel(), {isPlaceholder: true})],
    total: 0,
    page: 1,
  };
  public reelsPage: TimelinePageResult | null = null;
  public readonly reelsPosts$ = new BehaviorSubject<PostViewModel[]>([]);
  public readonly reelsPostSequence$ = this.reelsPosts$.asObservable();
  /** Canonical post instances prevent Media/Reels from retaining duplicate nested API graphs. */
  private readonly postModelsById = new Map<number, PostViewModel>();
  private readonly subscriptions = new Subscription();
  private feedScrollY = 0;
  /** Exposed to the static swipe-preview templates; it is never mutated by them. */
  public currentUser: CurrentUserViewModel | null = null;
  private browserReady = false;
  private reelsModalOpened = false;
  private reelsModalActive = false;
  private reelsModalSession = 0;
  /** Invalidates late pages when Reels closes or Home is destroyed. */
  private reelsCollectionSession = 0;
  private reelsPageLoadInFlight: Promise<boolean> | null = null;
  private reelsEndOfResults = false;
  private pendingSwipeTarget: HomeContentTab | null = null;
  private swipeHandoffActive = false;
  private swipeTrackSettled = false;
  private swipePromotionFrameId: number | null = null;
  private readonly swipePreviewMediaPrewarmLimit = 12;
  private readonly maxConcurrentSwipePreviewImageLoads = 2;
  private swipePreviewImagePrewarmVersion = 0;
  private swipePreviewImageUrlsKey = '';
  private swipePreviewImageQueue: string[] = [];
  /** Pending native image requests only; completed images are never retained. */
  private readonly swipePreviewImagesInFlight = new Set<HTMLImageElement>();

  constructor(
    private readonly seo: SeoService,
    private readonly translate: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly homeContentPrefetch: HomeContentPrefetchService,
    private readonly timelineDPS: TimelineDataProviderService,
    private readonly modalService: ModalService,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly videoService: VideoService,
    private readonly mediaOffline: MediaOfflineService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {
    afterNextRender(() => {
      this.browserReady = isPlatformBrowser(this.platformId);
      this.scheduleInactivePrefetch();
      this.tryOpenReelsModal();
    });
  }

  ngOnInit() {
    const title = this.translate.instant('common.home') === 'common.home'
      ? 'Home'
      : this.translate.instant('common.home');
    const description = this.translate.instant('meta.description') === 'meta.description'
      ? 'WTG | Watch This Garage.'
      : this.translate.instant('meta.description');
    const url = `${environment.appUrl}/`;
    this.seo.setTitle(title);
    this.seo.setDescription(description);
    this.seo.setOpenGraph({title, description, url, type: 'website'});
    this.seo.setCanonical(url);

    this.subscriptions.add(this.tokenDPS.currentUser.subscribe((user) => {
      this.currentUser = user;
    }));

    this.subscriptions.add(this.route.queryParamMap.subscribe((params) => {
      const nextTab = this.normalizeTab(params.get('tab'));
      const isPendingSwipeTarget = this.swipeHandoffActive && this.pendingSwipeTarget === nextTab;
      if (nextTab !== this.activeTab) {
        this.persistActiveTimelineState();
        if (this.activeTab === 'reels') {
          if (this.reelsModalActive) {
            // The Reels overlay belongs to this Home tab. Do not leave it alive
            // beneath another tab where its Swiper and video decoders would keep
            // consuming native renderer memory.
            this.modalService.forceClose();
            this.reelsModalActive = false;
            this.reelsModalSession += 1;
          }
          this.releaseReelsSession();
        }
        this.reelsModalOpened = false;
      }
      this.activeTab = nextTab;
      if (!isPendingSwipeTarget) {
        this.cancelSwipeHandoff();
        this.swipePreviewPanels = [];
        this.setActivePanel(nextTab);
      }
      this.ensureTabPage(nextTab);
      if (!isPendingSwipeTarget) {
        this.restoreActiveTabScroll(nextTab);
      }
      if (isPendingSwipeTarget && this.swipeTrackSettled) {
        this.promotePendingSwipePreview();
      }
      this.tryOpenReelsModal();
    }));
  }

  public selectTab(tab: HomeContentTab): void {
    if (tab === this.activeTab) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {tab: tab === 'timeline' ? null : tab},
      queryParamsHandling: 'merge',
    });
  }

  public get previousTabCount(): number {
    return Math.max(0, this.tabs.findIndex((tab) => tab.id === this.activeTab));
  }

  public get nextTabCount(): number {
    const activeIndex = this.tabs.findIndex((tab) => tab.id === this.activeTab);
    return activeIndex < 0 ? 0 : this.tabs.length - activeIndex - 1;
  }

  public onHomeTabSwipe({direction, steps}: HomeTabSwipeCommit): void {
    const targetTab = this.getAdjacentTab(direction, steps);
    if (!targetTab) {
      return;
    }

    this.pendingSwipeTarget = targetTab;
    this.swipeHandoffActive = true;
    this.swipeTrackSettled = false;
    this.tabTransitionDirection = null;
    this.selectTab(targetTab);
  }

  public onHomeTabSwipePreview(preview: HomeTabSwipePreview | null): void {
    this.swipePreviewPanels = preview
      ? this.getPreviewTabs(preview.direction, preview.visibleTabs, preview.exposedTabs || 1)
      : [];
    this.swipePreviewPanels.filter((panel) => panel.previewMediaEnabled).forEach(({tab}) => {
      this.ensureTabPage(tab);
      if (tab === 'reels') {
        // Only prepare the existing runtime; the preview never owns a player.
        this.preloadReelsPlayerRuntime();
      }
    });
    this.prewarmSwipePreviewMedia(preview);
    this.syncPreviewPanels();
  }

  public onHomeTabSwipeSettled(): void {
    if (!this.swipeHandoffActive || !this.pendingSwipeTarget) {
      return;
    }
    this.swipeTrackSettled = true;
    this.promotePendingSwipePreview();
  }

  private promotePendingSwipePreview(): void {
    if (!this.swipeHandoffActive || !this.pendingSwipeTarget || this.activeTab !== this.pendingSwipeTarget) {
      return;
    }
    const targetPanel = this.homeTabPanels.find((panel) => panel.id === this.pendingSwipeTarget);
    if (!targetPanel) {
      this.cancelSwipeHandoff();
      this.setActivePanel(this.activeTab);
      return;
    }
    // Keep the existing preview component at x=0 while its active lifecycle
    // attaches. Resetting the track before it becomes an active panel avoids
    // briefly applying the old negative track offset to this grid.
    targetPanel.renderMode = 'active';
    targetPanel.role = 'promoting';
    this.homeTabPanels = [targetPanel];
    this.swipePreviewPanels = [];
    this.swipePromotionFrameId = requestAnimationFrame(() => {
      this.swipePromotionFrameId = null;
      if (!this.swipeHandoffActive || this.pendingSwipeTarget !== targetPanel.id) {
        return;
      }
      this.homeTabSwipe?.completeSwipeHandoff();
      this.swipePromotionFrameId = requestAnimationFrame(() => {
        this.swipePromotionFrameId = null;
        if (!this.swipeHandoffActive || this.pendingSwipeTarget !== targetPanel.id) {
          return;
        }
        targetPanel.offsetPercent = 0;
        targetPanel.role = 'active';
        this.homeTabPanels = [targetPanel];
        this.clearSwipePreviewMediaPrewarm();
        this.pendingSwipeTarget = null;
        this.swipeHandoffActive = false;
        this.swipeTrackSettled = false;
        this.restoreActiveTabScroll(targetPanel.id);
        this.tryOpenReelsModal();
      });
    });
  }

  ngOnDestroy(): void {
    if (this.reelsModalActive) {
      this.modalService.forceClose();
      this.reelsModalActive = false;
      this.reelsModalSession += 1;
    }
    this.releaseReelsSession();
    this.subscriptions.unsubscribe();
    this.reelsPosts$.complete();
    this.cancelSwipeHandoff();
    this.postModelsById.clear();
  }

  public onTimelineStateChange(tab: 'timeline' | 'media', state: TimelineStateSnapshot): void {
    state.records = this.normalizePosts(state.records);
    this.timelineStates[tab] = state;
  }

  public onTimelineScrollChange(tab: 'timeline' | 'media', scrollY: number): void {
    const state = this.timelineStates[tab];
    if (state) {
      // Preserve the loaded-record reference. A scroll update must not create
      // a new Home snapshot or replay the Reels gallery sequence.
      state.scrollY = scrollY;
    }
  }

  /**
   * Exposed for development profiling through Angular devtools/component
   * inspection. It contains counts only and is intentionally not rendered.
   */
  public getRuntimeDiagnostics(): {
    activeTab: HomeContentTab;
    activeTimeline: ReturnType<TimelineComponent['getRuntimeDiagnostics']> | null;
    video: ReturnType<VideoService['getDiagnostics']>;
    mediaOffline: ReturnType<MediaOfflineService['getDiagnostics']>;
    snapshotRecords: Record<'timeline' | 'media' | 'reels', number>;
    previewPanels: number;
  } {
    return {
      activeTab: this.activeTab,
      activeTimeline: this.activeTimeline?.getRuntimeDiagnostics() || null,
      video: this.videoService.getDiagnostics(),
      mediaOffline: this.mediaOffline.getDiagnostics(),
      snapshotRecords: {
        timeline: this.timelineStates.timeline?.records.length || 0,
        media: this.timelineStates.media?.records.length || 0,
        reels: this.reelsPosts$.value.length,
      },
      previewPanels: this.swipePreviewPanels.length,
    };
  }

  public readonly loadNextReelsPage = (): Promise<boolean> => {
    if (
      this.activeTab !== 'reels'
      || !this.reelsPage
      || this.reelsEndOfResults
    ) {
      return Promise.resolve(false);
    }

    if (this.reelsPageLoadInFlight) {
      return this.reelsPageLoadInFlight;
    }

    const collectionSession = this.reelsCollectionSession;
    const nextPage = Math.max(2, (this.reelsPage.page || 1) + 1);
    const load = new Promise<boolean>((resolve) => {
      this.subscriptions.add(this.timelineDPS.getPostCollection(
        null,
        null,
        nextPage,
        this.mediaPostTypes,
        null,
        MediaTypeEnum.VIDEO,
      ).pipe(take(1)).subscribe({
        next: (page) => {
          if (collectionSession !== this.reelsCollectionSession || this.activeTab !== 'reels') {
            resolve(false);
            return;
          }

          const normalizedPage = this.normalizeTimelinePage(page);
          const currentPosts = this.reelsPosts$.value;
          const knownPostIds = new Set(currentPosts.map((post) => post.id));
          const appendedPosts = (normalizedPage.records || []).filter((post) => !knownPostIds.has(post.id));
          const records = [...currentPosts, ...appendedPosts];
          this.reelsPage = {...normalizedPage, records};
          this.reelsEndOfResults = !normalizedPage.records?.length || records.length >= (normalizedPage.total || 0);
          this.reelsPosts$.next(records);
          resolve(appendedPosts.length > 0);
        },
        error: () => resolve(false),
      }));
    });
    this.reelsPageLoadInFlight = load;
    void load.then(() => {
      if (this.reelsPageLoadInFlight === load) {
        this.reelsPageLoadInFlight = null;
      }
    });
    return load;
  };

  public readonly hasNextReelsPage = (): boolean => {
    return this.activeTab === 'reels'
      && !!this.reelsPage
      && !this.reelsEndOfResults
      && !this.reelsPageLoadInFlight;
  };

  public getReelsShellPoster(): string | null {
    const media = this.reelsPage?.records
      ?.flatMap((post) => post.media || [])
      .find((item) => item?.type === MediaTypeEnum.VIDEO);
    return media
      ? MediaObjectPipe.transformToLink(media, {formatType: 'm3u8_timeline', defaultImageType: 'loading'}) || null
      : null;
  }

  private persistActiveTimelineState(): void {
    if (this.activeTab === 'feed') {
      this.feedScrollY = this.getScrollY();
      return;
    }
    if (this.activeTab === 'reels') {
      // Reels owns a gallery session rather than a Timeline instance or
      // scroll snapshot.
      return;
    }
    const state = this.activeTimeline?.captureState();
    if (state) {
      this.onTimelineStateChange(this.activeTab, state);
    }
  }

  private restoreActiveTabScroll(tab: HomeContentTab): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    requestAnimationFrame(() => {
      if (this.activeTab !== tab) {
        return;
      }
      if (tab === 'feed') {
        window.scrollTo({top: this.feedScrollY, behavior: 'auto'});
        return;
      }
      if (tab === 'reels') {
        window.scrollTo({top: 0, behavior: 'auto'});
        return;
      }
      // A restored timeline applies its saved position once virtualization has
      // calculated its spacers. A fresh tab must not inherit another tab's
      // scroll offset, otherwise it can trigger premature pagination.
      if (!this.timelineStates[tab]?.ready) {
        window.scrollTo({top: 0, behavior: 'auto'});
      }
    });
  }

  private getScrollY(): number {
    return window.scrollY || document.documentElement?.scrollTop || 0;
  }

  private getAdjacentTab(direction: HomeTabSwipeDirection, steps = 1): HomeContentTab | null {
    const activeIndex = this.tabs.findIndex((tab) => tab.id === this.activeTab);
    return this.tabs[activeIndex + (direction === 'left' ? steps : -steps)]?.id || null;
  }

  private getPreviewTabs(
    direction: HomeTabSwipeDirection,
    count: number,
    exposedTabs: number,
  ): Array<{tab: HomeContentTab; offsetPercent: number; previewMediaEnabled: boolean}> {
    const activeIndex = this.tabs.findIndex((tab) => tab.id === this.activeTab);
    const step = direction === 'left' ? 1 : -1;
    const previewPanels: Array<{tab: HomeContentTab; offsetPercent: number; previewMediaEnabled: boolean}> = [];
    for (let index = 0; index < count; index++) {
      const position = index + 1;
      const tab = this.tabs[activeIndex + position * step]?.id;
      if (!tab) {
        break;
      }
      previewPanels.push({
        tab,
        offsetPercent: position * step * 100,
        previewMediaEnabled: position <= exposedTabs,
      });
    }
    return previewPanels;
  }

  public trackByHomeTabPanel(_: number, panel: HomeTabPanel): HomeContentTab {
    return panel.id;
  }

  private setActivePanel(tab: HomeContentTab): void {
    this.homeTabPanels = [{id: tab, offsetPercent: 0, renderMode: 'active', role: 'active', previewMediaEnabled: true}];
  }

  private syncPreviewPanels(): void {
    const activePanel = this.homeTabPanels.find((panel) => panel.role === 'active')
      || {id: this.activeTab, offsetPercent: 0, renderMode: 'active' as const, role: 'active' as const, previewMediaEnabled: true};
    const existingPanels = new Map(this.homeTabPanels.map((panel) => [panel.id, panel]));
    this.homeTabPanels = [activePanel, ...this.swipePreviewPanels.map(({tab, offsetPercent}) => {
      const existing = existingPanels.get(tab);
      if (existing) {
        existing.offsetPercent = offsetPercent;
        existing.previewMediaEnabled = this.swipePreviewPanels.find((panel) => panel.tab === tab)?.previewMediaEnabled ?? true;
        return existing;
      }
      return {
        id: tab,
        offsetPercent,
        renderMode: 'swipe-preview' as const,
        role: 'preview' as const,
        previewMediaEnabled: this.swipePreviewPanels.find((panel) => panel.tab === tab)?.previewMediaEnabled ?? true,
      };
    })];
  }

  private cancelSwipeHandoff(): void {
    if (this.swipePromotionFrameId !== null) {
      cancelAnimationFrame(this.swipePromotionFrameId);
      this.swipePromotionFrameId = null;
    }
    this.homeTabSwipe?.completeSwipeHandoff();
    this.pendingSwipeTarget = null;
    this.swipeHandoffActive = false;
    this.swipeTrackSettled = false;
    this.clearSwipePreviewMediaPrewarm();
  }

  /**
   * Start the network request before Angular mounts the swipe-preview panel.
   * Only its first thumbnail is needed per grid tile; Video posters use the
   * same image path, never a manifest or decoder. Native Image objects are
   * released as soon as they settle, so this does not keep a bitmap cache.
   */
  private prewarmSwipePreviewMedia(preview: HomeTabSwipePreview | null): void {
    if (!preview || !isPlatformBrowser(this.platformId)) {
      this.clearSwipePreviewMediaPrewarm();
      return;
    }

    const urls = Array.from(new Set(this.getPreviewTabs(preview.direction, preview.visibleTabs, preview.exposedTabs || 1)
      .filter((panel) => panel.tab !== 'reels')
      .flatMap((panel) => this.getTabPreviewPosts(panel.tab))
      .flatMap((post) => post?.media?.slice(0, 1) || [])
      .map((media) => media?.type === MediaTypeEnum.VIDEO
        ? MediaObjectPipe.transformToLink(media, {formatType: 'm3u8_timeline'})
        : MediaObjectPipe.transformToLink(media, {formatType: 'thumb_md', defaultImageType: 'background'}))
      .filter((url): url is string => !!url && !url.startsWith('assets/'))
      .slice(0, this.swipePreviewMediaPrewarmLimit)));

    if (!urls.length) {
      this.clearSwipePreviewMediaPrewarm();
      return;
    }

    const urlsKey = urls.join('|');
    if (urlsKey === this.swipePreviewImageUrlsKey) {
      return;
    }
    this.clearSwipePreviewMediaPrewarm();
    this.swipePreviewImageUrlsKey = urlsKey;
    this.swipePreviewImageQueue = urls;
    this.drainSwipePreviewImageQueue(this.swipePreviewImagePrewarmVersion);
  }

  private getTabPreviewPosts(tab: HomeContentTab): PostViewModel[] {
    switch (tab) {
      case 'timeline':
        return this.timelineStates.timeline?.ready ? this.timelineStates.timeline.records : this.timelinePage?.records || [];
      case 'media':
        return this.timelineStates.media?.ready ? this.timelineStates.media.records : this.mediaPage?.records || [];
      default:
        return [];
    }
  }

  private clearSwipePreviewMediaPrewarm(): void {
    this.swipePreviewImagePrewarmVersion += 1;
    this.swipePreviewImageUrlsKey = '';
    this.swipePreviewImageQueue = [];
    this.swipePreviewImagesInFlight.forEach((image) => {
      image.onload = null;
      image.onerror = null;
      image.src = '';
    });
    this.swipePreviewImagesInFlight.clear();
  }

  private drainSwipePreviewImageQueue(version: number): void {
    while (
      version === this.swipePreviewImagePrewarmVersion
      && this.swipePreviewImagesInFlight.size < this.maxConcurrentSwipePreviewImageLoads
      && this.swipePreviewImageQueue.length
    ) {
      const url = this.swipePreviewImageQueue.shift()!;
      const image = new Image();
      image.decoding = 'async';
      this.swipePreviewImagesInFlight.add(image);
      const settle = () => {
        image.onload = null;
        image.onerror = null;
        this.swipePreviewImagesInFlight.delete(image);
        if (version === this.swipePreviewImagePrewarmVersion) {
          this.drainSwipePreviewImageQueue(version);
        }
      };
      image.onload = settle;
      image.onerror = settle;
      image.src = url;
    }
  }

  private ensureTabPage(tab: HomeContentTab): void {
    const hasPage = tab === 'timeline' ? this.timelineStates.timeline?.ready || !!this.timelinePage
      : tab === 'media' ? this.timelineStates.media?.ready || !!this.mediaPage
      : tab === 'feed' ? !!this.feedPage : !!this.reelsPage;
    if (this.subscriptions.closed || this.initialPageRequested[tab] || hasPage) {
      return;
    }
    this.initialPageRequested[tab] = true;
    this.preloadPending[tab] = true;

    switch (tab) {
      case 'feed':
        this.subscriptions.add(this.homeContentPrefetch.getFeedPage().subscribe({
          next: (page) => {
            this.feedPage = page;
          },
          error: () => this.handleInitialPageError('feed'),
          complete: () => {
            this.preloadPending.feed = false;
            this.homeContentPrefetch.release('feed');
          },
        }));
        return;
      case 'media':
        this.subscriptions.add(this.homeContentPrefetch.getMediaPage().subscribe({
          next: (page) => this.mediaPage = this.normalizeTimelinePage(page),
          error: () => this.handleInitialPageError('media'),
          complete: () => {
            this.preloadPending.media = false;
            this.homeContentPrefetch.release('media');
          },
        }));
        return;
      case 'reels': {
        const reelsCollectionSession = this.reelsCollectionSession;
        this.subscriptions.add(this.homeContentPrefetch.getReelsPage().subscribe({
          next: (page) => {
            if (reelsCollectionSession !== this.reelsCollectionSession) {
              return;
            }
            this.adoptReelsPage(page);
            this.preloadReelsPlayerRuntime();
            this.tryOpenReelsModal();
          },
          error: () => {
            if (reelsCollectionSession === this.reelsCollectionSession) {
              this.handleInitialPageError('reels');
            }
          },
          complete: () => {
            if (reelsCollectionSession !== this.reelsCollectionSession) {
              return;
            }
            this.preloadPending.reels = false;
            this.homeContentPrefetch.release('reels');
          },
        }));
        return;
      }
      default:
        this.subscriptions.add(this.homeContentPrefetch.getTimelinePage().subscribe({
          next: (page) => this.timelinePage = this.normalizeTimelinePage(page),
          error: () => this.handleInitialPageError('timeline'),
          complete: () => {
            this.preloadPending.timeline = false;
            this.homeContentPrefetch.release('timeline');
          },
        }));
    }
  }

  private handleInitialPageError(tab: HomeContentTab): void {
    this.preloadPending[tab] = false;
    this.initialPageRequested[tab] = false;
    this.homeContentPrefetch.release(tab);
  }

  private scheduleInactivePrefetch(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const prefetch = () => {
      if (this.subscriptions.closed) {
        return;
      }
      // Use the same ownership/pending flags for idle prefetch and gestures.
      // Timeline must also be ready when Home was opened on another tab.
      this.ensureTabPage('timeline');
      this.ensureTabPage('feed');
      this.ensureTabPage('media');
      this.preloadReelsPlayerRuntime();
    };
    const browserWindow = window as Window & {requestIdleCallback?: (callback: () => void, options?: {timeout: number}) => number};
    if (browserWindow.requestIdleCallback) {
      browserWindow.requestIdleCallback(prefetch, {timeout: 1500});
      return;
    }
    window.setTimeout(prefetch, 250);
  }

  private normalizeTab(value: string | null): HomeContentTab {
    return value === 'feed' || value === 'media' || value === 'reels' || value === 'timeline' ? value : 'timeline';
  }

  private preloadReelsPlayerRuntime(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    void this.videoService.preloadPlayerRuntime().catch(() => undefined);
  }

  private normalizeTimelinePage(page: TimelinePageResult): TimelinePageResult {
    return {...page, records: this.normalizePosts(page.records || [])};
  }

  private normalizePosts(posts: PostViewModel[]): PostViewModel[] {
    return (posts || []).map((post) => {
      if (!post?.id || post.isPlaceholder) {
        return post;
      }
      const existing = this.postModelsById.get(post.id);
      if (existing) {
        return existing;
      }
      this.postModelsById.set(post.id, post);
      return post;
    });
  }

  private adoptReelsPage(page: TimelinePageResult): void {
    const normalizedPage = this.normalizeTimelinePage(page);
    this.reelsPage = normalizedPage;
    this.reelsEndOfResults = !normalizedPage.records?.length
      || normalizedPage.records.length >= (normalizedPage.total || 0);
    this.reelsPosts$.next(normalizedPage.records || []);
  }

  private releaseReelsSession(): void {
    this.reelsCollectionSession += 1;
    this.reelsPageLoadInFlight = null;
    this.reelsEndOfResults = false;
    this.reelsPage = null;
    this.reelsPosts$.next([]);
    this.initialPageRequested.reels = false;
    this.preloadPending.reels = false;
    this.homeContentPrefetch.release('reels');
    this.pruneCanonicalPosts();
  }

  private pruneCanonicalPosts(): void {
    const retainedPostIds = new Set<number>([
      ...(this.timelinePage?.records || []),
      ...(this.mediaPage?.records || []),
      ...(this.timelineStates.timeline?.records || []),
      ...(this.timelineStates.media?.records || []),
    ].map((post) => post.id));
    this.postModelsById.forEach((_, postId) => {
      if (!retainedPostIds.has(postId)) {
        this.postModelsById.delete(postId);
      }
    });
  }

  private tryOpenReelsModal(): void {
    if (
      !this.browserReady
      || this.activeTab !== 'reels'
      || this.reelsModalOpened
      || !this.reelsPage?.records?.length
      || this.swipeHandoffActive
      || !this.isReelsPanelActive()
    ) {
      return;
    }

    this.reelsModalOpened = true;
    this.reelsModalActive = true;
    const modalSession = ++this.reelsModalSession;
    const galleryModal = this.modalService.showOverlayGalleryModal({
      post: this.reelsPage.records[0],
      posts: this.reelsPosts$.value,
      postIndex: 0,
      postSequence$: this.reelsPostSequence$,
      loadNextPostPage: this.loadNextReelsPage,
      hasNextPostPage: this.hasNextReelsPage,
      visibleMediaType: MediaTypeEnum.VIDEO,
      postPageSize: 9,
      prefetchAtPostIndex: 1,
      currentUser: this.currentUser,
      component: null,
      activeIndex: 0,
      initialCurrentTime: 0,
      initialWasPlaying: false,
      sourceVideoKey: null,
    });
    this.subscriptions.add(galleryModal.successState.pipe(take(1)).subscribe({
      next: () => {
        if (modalSession === this.reelsModalSession && this.activeTab === 'reels') {
          this.reelsModalActive = false;
          this.reelsModalOpened = false;
          this.reelsModalSession += 1;
          this.releaseReelsSession();
          void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {tab: 'media'},
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
      },
      complete: () => {
        if (modalSession === this.reelsModalSession) {
          this.reelsModalActive = false;
        }
      },
    }));
  }

  private isReelsPanelActive(): boolean {
    return this.homeTabPanels.length === 1
      && this.homeTabPanels[0].id === 'reels'
      && this.homeTabPanels[0].role === 'active';
  }
}
